import { useCreateNoteBo, useGetNotesBo } from "@/api/notes.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Textarea } from "@/components/ui/textarea";
import {
  ExpeditionNotesDto,
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { format } from "date-fns";
import { Loader2, StickyNote } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { NoteType } from "@/constants/enums/noteType.enum";
import {
  formatQualificationDate,
  getLateShippingQualificationOption,
} from "@/components/backOffice/shared/lateShippingQualificationBadge";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

export const NotesModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation("shared");
  const [addNote, setAddNote] = useState<boolean>(false);
  const { data: notesData, isLoading: isLoadingNotes } = useGetNotesBo(expedition.id);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t("notesModal.title")}
        size="md"
        contentClassName="bg-[#F8FAFC] max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:w-screen max-md:max-w-none max-md:h-dvh max-md:max-h-none max-md:rounded-none max-md:content-start"
        dismissible
      >
        <Button variant="default" onClick={() => setAddNote(true)} className="w-full">
          {t("notesModal.addNote")}
        </Button>
        {isLoadingNotes ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-6 animate-spin text-gray-500" />
          </div>
        ) : notesData && notesData.length > 0 ? (
          <div className="flex flex-col gap-3">
            {notesData.map((note, index) => (
              <ExpeditionNoteItem key={index} note={note} />
            ))}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {t("notesModal.empty")}
          </div>
        )}
      </Modal>
      {addNote && (
        <NewNoteModal expedition={expedition} onClose={() => setAddNote(false)} open={true} />
      )}
    </>
  );
};

const ExpeditionNoteItem = ({ note }: { note: ExpeditionNotesDto }) => {
  const { t } = useTranslation("shared");
  const isDimensions = note.type === NoteType.PACKAGING_DIMENSIONS;
  const qualificationOption =
    note.type === NoteType.LATE_SHIPPING_QUALIFICATION
      ? getLateShippingQualificationOption(note.subType)
      : undefined;

  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        isDimensions ? "border-orange-300 bg-orange-50" : "border-gray-200 bg-white",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <StickyNote
          className={cn("h-3.5 w-3.5 shrink-0", isDimensions ? "text-orange-500" : "text-gray-400")}
        />
        <span
          className={cn("text-xs font-medium", isDimensions ? "text-orange-800" : "text-gray-700")}
        >
          {note.createdBy.firstName} {note.createdBy.lastName}
        </span>
        <span className={cn("text-xs", isDimensions ? "text-orange-500" : "text-gray-400")}>
          {format(note.createdAt, "dd/MM/yyyy HH:mm")}
        </span>
      </div>
      {qualificationOption && (
        <p className="mb-1 text-xs font-semibold text-gray-500">
          {t("notesModal.lateShippingQualification")}
        </p>
      )}
      <p
        className={cn(
          "overflow-hidden text-sm break-words",
          isDimensions ? "text-orange-900" : "text-gray-800",
        )}
      >
        {qualificationOption
          ? qualificationOption.badgeLabel(formatQualificationDate(note.note))
          : note.note}
      </p>
    </div>
  );
};

export const NewNoteModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation("shared");
  const [note, setNote] = useState<string | undefined>(undefined);
  const { mutateAsync: createNote, isPending } = useCreateNoteBo();

  const handleNewNote = async () => {
    if (!note) return;
    await createNote({ id: expedition.id, payload: note.trim() });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("newNoteModal.title")}
      size="md"
      dismissible
      contentClassName="bg-[#F8FAFC] max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:w-screen max-md:max-w-none max-md:h-dvh max-md:max-h-none max-md:rounded-none max-md:content-start"
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button
            variant="default"
            onClick={handleNewNote}
            className="flex-1"
            disabled={isPending || !note}
          >
            {t("newNoteModal.submit")}
          </Button>
        </div>
      }
    >
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("newNoteModal.notePlaceholder")}
        className="min-h-32"
      />
    </Modal>
  );
};
